class InfiniteScrollManager {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.minLoadedPage = 1;
    this.maxLoadedPage = 1;
    this.pagesLoaded = 1;
    this.isLoading = false;
    this.totalPages = 1;
    this.observers = [];

    const trigger = section.querySelector('infinite-scroll[data-trigger="infinite"]');

    this.trigger = trigger;
    this.totalPages =
      parseInt(trigger?.dataset.totalPages, 10) ||
      parseInt(section.dataset.totalPages, 10) ||
      1;
    this.productsPerPage =
      parseInt(trigger?.dataset.productsPerPage, 10) ||
      parseInt(section.dataset.productsPerPage, 10) ||
      24;
    this.totalProducts =
      parseInt(trigger?.dataset.totalProducts, 10) ||
      parseInt(section.dataset.totalProducts, 10) ||
      0;

    const currentPage =
      parseInt(trigger?.dataset.currentPage, 10) ||
      parseInt(section.dataset.currentPage, 10) ||
      this.getPageFromUrl();

    this.minLoadedPage = currentPage;
    this.maxLoadedPage = currentPage;

    this.stampPage(currentPage);
    this.setupInfiniteScroll();
    this.setupPrevButton();
    this.updateProgress();
  }

  getPageFromUrl() {
    const page = parseInt(new URLSearchParams(window.location.search).get("page"), 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }

  getProductList() {
    return (
      this.section.querySelector(`#product-list-${this.sectionId}`) ||
      this.section.querySelector("product-list.product-list")
    );
  }

  // Her kart geldiği sayfayı taşır: hp-collection-scroll-restore.js tıklanan ürünün sayfasını buradan okur.
  stampPage(pageNumber) {
    const productList = this.getProductList();

    if (!productList) {
      return;
    }

    Array.from(productList.children).forEach((item) => {
      if (!item.dataset.hpPage) {
        item.dataset.hpPage = String(pageNumber);
      }
    });
  }

  setupInfiniteScroll() {
    if (!this.trigger || this.maxLoadedPage >= this.totalPages) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading) {
            this.loadNextPage(this.trigger);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px 350px 0px",
        threshold: 0,
      }
    );

    observer.observe(this.trigger);
    this.observers.push(observer);
  }

  setupPrevButton() {
    const prevButton = this.section.querySelector(".js-show-prev-products");

    if (!prevButton || prevButton.dataset.infiniteScrollBound === "true") {
      return;
    }

    prevButton.dataset.infiniteScrollBound = "true";
    prevButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.loadPrevPage(prevButton);
    });
  }

  async loadNextPage(element) {
    if (this.isLoading || this.maxLoadedPage >= this.totalPages) {
      return;
    }

    const nextUrl = element.getAttribute("data-url");

    if (!nextUrl) {
      return;
    }

    this.isLoading = true;
    element.classList.add("is-loading");

    try {
      const nextPage = this.maxLoadedPage + 1;
      const doc = await this.fetchDocument(nextUrl);
      const newProducts = this.extractProductListHtml(doc);

      if (!newProducts) {
        element.remove();
        return;
      }

      const currentGrid = this.getProductList();

      if (!currentGrid) {
        return;
      }

      currentGrid.insertAdjacentHTML("beforeend", newProducts);
      this.stampPage(nextPage);

      this.maxLoadedPage = nextPage;
      this.pagesLoaded += 1;
      this.updateUrl(nextPage);
      this.updateProgress();
      this.revealNewProducts();

      const newTrigger = doc.querySelector('infinite-scroll[data-trigger="infinite"]');

      if (newTrigger?.getAttribute("data-url")) {
        element.setAttribute("data-url", newTrigger.getAttribute("data-url"));
      } else {
        element.remove();
        this.disconnectObservers();
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Infinite scroll failed to load next page", error);
      }
    } finally {
      this.isLoading = false;
      element.classList.remove("is-loading");
    }
  }

  async loadPrevPage(button) {
    if (this.isLoading || this.minLoadedPage <= 1) {
      return;
    }

    this.isLoading = true;
    button.setAttribute("aria-busy", "true");
    button.disabled = true;

    const scrollHeightBefore = document.documentElement.scrollHeight;
    const scrollY = window.scrollY;

    try {
      const prevPage = this.minLoadedPage - 1;
      const doc = await this.fetchDocument(this.buildPageUrl(prevPage));
      const newProducts = this.extractProductListHtml(doc);
      const currentGrid = this.getProductList();

      if (!newProducts) {
        console.error("Infinite scroll: previous page product list not found");
        return;
      }

      if (!currentGrid) {
        console.error("Infinite scroll: current product list not found");
        return;
      }

      currentGrid.insertAdjacentHTML("afterbegin", newProducts);
      this.stampPage(prevPage);

      const scrollHeightAfter = document.documentElement.scrollHeight;
      window.scrollTo(0, scrollY + (scrollHeightAfter - scrollHeightBefore));

      this.minLoadedPage = prevPage;
      this.pagesLoaded += 1;
      this.updateUrl(prevPage);
      this.updateProgress();
      // Üste eklenen sayfada ekrana en yakın kartlar sondakiler: sıralama sondan başlar.
      this.revealNewProducts("last");

      if (prevPage === 1) {
        button.closest(".infinite-scroll-prev")?.remove();
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Infinite scroll failed to load previous page", error);
      }
    } finally {
      this.isLoading = false;

      if (button.isConnected) {
        button.removeAttribute("aria-busy");
        button.disabled = false;
      }
    }
  }

  async fetchDocument(url) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, "text/html");
  }

  extractProductListHtml(doc) {
    const productList =
      doc.querySelector(`#product-list-${this.sectionId}`) ||
      doc.querySelector(".shopify-section--main-collection product-list.product-list") ||
      doc.querySelector("product-list.product-list");

    return productList?.innerHTML?.trim() || "";
  }

  buildPageUrl(pageNumber) {
    const url = new URL(window.location.href);

    if (pageNumber === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", String(pageNumber));
    }

    return url.toString();
  }

  updateUrl(pageNumber) {
    history.replaceState(null, "", this.buildPageUrl(pageNumber));
  }

  updateProgress() {
    const progressElement = this.section.querySelector(".collection-progress");

    if (!progressElement) {
      return;
    }

    const productsPerPage = parseInt(progressElement.dataset.productsPerPage, 10) || this.productsPerPage;
    const totalProducts = parseInt(progressElement.dataset.totalProducts, 10) || this.totalProducts;
    const loadedProducts = Math.min(this.pagesLoaded * productsPerPage, totalProducts);
    const progressText = progressElement.querySelector(".collection-progress__text");
    const progressFill = progressElement.querySelector(".collection-progress__fill");

    if (progressText) {
      progressText.textContent = `${loadedProducts} / ${totalProducts}`;
    }

    if (progressFill && totalProducts > 0) {
      progressFill.style.width = `${(loadedProducts / totalProducts) * 100}%`;
    }
  }

  // Yalnızca yeni eklenen kartlar açılır. Argümansız `reveal()` listedeki TÜM kartları baştan sıralı animasyona
  // soktuğu için (gecikme kart sayısıyla büyür: 2. sayfada ~2-3 sn, 3. sayfada ~3,5-4 sn) yeni sayfanın kartları
  // o süre boyunca `opacity: 0` kalıyor, spinner'dan sonra ekranda uzun bir boş alan görünüyordu.
  revealNewProducts(from = "first") {
    const productList = this.getProductList();

    if (!productList || typeof productList.reveal !== "function") {
      return;
    }

    productList.reveal({ onlyNew: true, start: 0, from });
  }

  disconnectObservers() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }

  destroy() {
    this.disconnectObservers();
    this.section = null;
    this.trigger = null;
  }
}

let collectionInfiniteScrollManager = null;

function initCollectionInfiniteScroll() {
  if (collectionInfiniteScrollManager) {
    collectionInfiniteScrollManager.destroy();
    collectionInfiniteScrollManager = null;
  }

  const section = document.querySelector(".shopify-section--main-collection [data-collection-infinite-scroll]");

  if (!section) {
    return;
  }

  collectionInfiniteScrollManager = new InfiniteScrollManager(section);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCollectionInfiniteScroll);
} else {
  initCollectionInfiniteScroll();
}

// QA FLT-02 / LST-02: theme.js `facet:update` içinde `theme:loading:end` olayı, bölümün DOM'u
// (`replaceChildren`) yenilenmeden ÖNCE tetikleniyor. Anında yapılan init eski (birazdan silinecek)
// tetikleyiciye bağlandığı için yeni <infinite-scroll> hiç gözlemlenmiyordu: filtre / sıralama sonrası
// sonraki sayfalar yüklenmiyor ve alttaki spinner öylece kalıyordu. Init, DOM yenilendikten sonraya ertelenir.
let collectionInfiniteScrollInitTimer = null;

function scheduleCollectionInfiniteScrollInit() {
  window.clearTimeout(collectionInfiniteScrollInitTimer);
  collectionInfiniteScrollInitTimer = window.setTimeout(initCollectionInfiniteScroll, 0);
}

function observeMainCollectionSection() {
  const mainCollectionSection = document.querySelector(".shopify-section--main-collection");

  if (!mainCollectionSection || mainCollectionSection.dataset.infiniteScrollObserved === "true") {
    return;
  }

  mainCollectionSection.dataset.infiniteScrollObserved = "true";

  // Olay sırasından bağımsız güvence: bölümün içeriği değiştiğinde tetikleyici kopmuşsa yeniden bağlan.
  new MutationObserver(() => {
    if (!collectionInfiniteScrollManager?.trigger?.isConnected) {
      scheduleCollectionInfiniteScrollInit();
    }
  }).observe(mainCollectionSection, { childList: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", observeMainCollectionSection);
} else {
  observeMainCollectionSection();
}

document.addEventListener("shopify:section:load", initCollectionInfiniteScroll);
document.addEventListener("theme:loading:end", scheduleCollectionInfiniteScrollInit);
